// File: components/ThreadRow.jsx
//
// One row in a list of message threads (title, last message preview,
// and a date on the right). Used in two places:
//   - The "Your Messages" widget on the Home page
//   - The thread sidebar on the Messages page
//
// Props:
//   thread   - the thread data (title, lastMessage, updatedAt, status)
//   active   - true if this row is the currently-selected thread
//              (it gets a green-tinted background)
//   onClick  - what to run when the row is clicked
//
// Closed threads automatically get a yellow "Closed" pill next to the
// title — no extra prop needed.

import React from "react";
import { COLORS, THEME } from "../theme";
import { formatThreadTime } from "../utils/format";

export default function ThreadRow({ thread, active = false, onClick }) {
  // A thread can be "open" or "closed". Default to "open" if missing.
  const closed = (thread.status || "open") === "closed";

  return (
    <button
      onClick={onClick}
      type="button"
      className="w-full text-left rounded-xl border transition px-3 py-2 backdrop-blur hover:-translate-y-0.5"
      style={{
        backgroundColor: active
          ? COLORS.greenLight
          : "rgba(255,255,255,0.86)",
        borderColor: active ? "rgba(64,115,0,0.35)" : THEME.border,
        boxShadow: active
          ? "0 10px 22px rgba(31,56,0,0.12)"
          : "0 8px 20px rgba(31,56,0,0.06)",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div
            className="text-sm font-extrabold truncate"
            style={{ color: THEME.text }}
          >
            {thread.title || "Message Thread"}
            {closed ? (
              <span
                className="ml-2 text-[11px] font-extrabold px-2 py-0.5 rounded-full border"
                style={{
                  color: COLORS.brown,
                  backgroundColor: COLORS.yellowLight,
                  borderColor: "rgba(61,51,0,0.18)",
                }}
              >
                Closed
              </span>
            ) : null}
          </div>

          <div
            className="text-[12px] truncate"
            style={{ color: "rgba(31,56,0,0.58)" }}
          >
            {thread.lastMessage || "No messages yet"}
          </div>
        </div>

        <div
          className="text-[11px] shrink-0"
          style={{ color: COLORS.aquaDark }}
        >
          {formatThreadTime(thread.updatedAt)}
        </div>
      </div>
    </button>
  );
}
